import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import QRCode from 'qrcode';

const ActividadesExtracurriculares = () => {
  const { user, API } = useContext(AuthContext);
  const [actividades, setActividades] = useState([]);
  const [participantes, setParticipantes] = useState([]); // Estudiantes + Profesores
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('activas');
  
  // Estados para modal de creación/edición
  const [showModal, setShowModal] = useState(false);
  const [actividadEditando, setActividadEditando] = useState(null);
  const [form, setForm] = useState({
    titulo: '',
    descripcion: '',
    fecha_inicio: '',
    hora_inicio: '09:00',
    fecha_fin: '',
    hora_fin: '12:00',
    ubicacion: '',
    tipo: 'actividad',
    destinatarios: 'todos',
    estudiantes_seleccionados: []
  });
  const [saving, setSaving] = useState(false);
  
  // Estados para modal de detalle
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actividadDetalle, setActividadDetalle] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');

  const canCreate = user?.rol === 'admin' || user?.rol === 'profesor';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [actividadesRes, usersRes] = await Promise.all([
        axios.get(`${API}/actividades-extracurriculares`, { headers }),
        canCreate ? axios.get(`${API}/users`, { headers }).catch((e) => { console.error('Error cargando usuarios:', e); return { data: [] }; }) : Promise.resolve({ data: [] })
      ]);
      
      setActividades(actividadesRes.data || []);
      
      // Filtrar estudiantes y profesores activos
      const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
      const participantesActivos = usersData.filter(u => 
        ['estudiante', 'pastor', 'graduado', 'profesor'].includes(u.rol) && (u.activo === true || u.estado === 'activo')
      ).sort((a, b) => {
        // Ordenar: primero profesores, luego estudiantes alfabéticamente
        if (a.rol === 'profesor' && b.rol !== 'profesor') return -1;
        if (a.rol !== 'profesor' && b.rol === 'profesor') return 1;
        return `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`);
      });
      setParticipantes(participantesActivos);
      console.log('Participantes cargados:', participantesActivos.length);
    } catch (error) {
      console.error('Error en loadData:', error);
      toast.error('Error al cargar actividades');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    setForm({
      titulo: '',
      descripcion: '',
      fecha_inicio: todayStr,
      hora_inicio: '09:00',
      fecha_fin: todayStr,
      hora_fin: '12:00',
      ubicacion: '',
      tipo: 'actividad',
      destinatarios: 'todos',
      estudiantes_seleccionados: []
    });
    setActividadEditando(null);
  };

  const openCrear = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditar = (actividad) => {
    const fechaInicio = new Date(actividad.fecha_inicio);
    const fechaFin = new Date(actividad.fecha_fin);
    
    setForm({
      titulo: actividad.titulo,
      descripcion: actividad.descripcion,
      fecha_inicio: format(fechaInicio, 'yyyy-MM-dd'),
      hora_inicio: format(fechaInicio, 'HH:mm'),
      fecha_fin: format(fechaFin, 'yyyy-MM-dd'),
      hora_fin: format(fechaFin, 'HH:mm'),
      ubicacion: actividad.ubicacion || '',
      tipo: actividad.tipo || 'actividad',
      destinatarios: actividad.destinatarios || 'todos',
      estudiantes_seleccionados: actividad.estudiantes_seleccionados || []
    });
    setActividadEditando(actividad);
    setShowModal(true);
  };

  const handleGuardar = async () => {
    if (!form.titulo.trim() || !form.descripcion.trim()) {
      toast.error('El título y la descripción son obligatorios');
      return;
    }
    
    if (!form.fecha_inicio || !form.fecha_fin) {
      toast.error('Las fechas son obligatorias');
      return;
    }

    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const fechaInicioCompleta = `${form.fecha_inicio}T${form.hora_inicio}:00`;
      const fechaFinCompleta = `${form.fecha_fin}T${form.hora_fin}:00`;
      
      const payload = {
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim(),
        fecha_inicio: fechaInicioCompleta,
        fecha_fin: fechaFinCompleta,
        ubicacion: form.ubicacion.trim() || null,
        tipo: form.tipo,
        destinatarios: form.destinatarios,
        estudiantes_seleccionados: form.destinatarios === 'seleccionados' ? form.estudiantes_seleccionados : []
      };

      if (actividadEditando) {
        await axios.put(`${API}/actividades-extracurriculares/${actividadEditando.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Actividad actualizada correctamente');
      } else {
        await axios.post(`${API}/actividades-extracurriculares`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('¡Actividad creada! Los estudiantes recibirán notificaciones automáticas.');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la actividad');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = async (actividadId) => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar esta actividad? Los participantes serán notificados.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/actividades-extracurriculares/${actividadId}`, 
        { estado: 'cancelado' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Actividad cancelada');
      loadData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Error al cancelar la actividad');
    }
  };

  const handleCambiarEstado = async (actividadId, nuevoEstado, mensaje) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/actividades-extracurriculares/${actividadId}`, 
        { estado: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(mensaje);
      loadData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Error al cambiar el estado');
    }
  };

  const handleEliminar = async (actividadId) => {
    if (!window.confirm('¿Eliminar permanentemente esta actividad? Esta acción no se puede deshacer.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/actividades-extracurriculares/${actividadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Actividad eliminada');
      loadData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error('Error al eliminar la actividad');
    }
  };

  const openDetalle = async (actividad) => {
    setActividadDetalle(actividad);
    setShowDetailModal(true);
    
    try {
      // Generar QR con el link completo para marcar asistencia
      const baseUrl = window.location.origin;
      const asistenciaUrl = `${baseUrl}/asistencia-actividad/${actividad.id}`;
      const qrUrl = await QRCode.toDataURL(asistenciaUrl, {
        width: 250,
        margin: 2,
        color: { dark: '#166534', light: '#ffffff' }
      });
      setQrCodeUrl(qrUrl);
    } catch (err) {
      console.error('Error generando QR:', err);
    }
  };

  const toggleEstudiante = (estudianteId) => {
    setForm(prev => ({
      ...prev,
      estudiantes_seleccionados: prev.estudiantes_seleccionados.includes(estudianteId)
        ? prev.estudiantes_seleccionados.filter(id => id !== estudianteId)
        : [...prev.estudiantes_seleccionados, estudianteId]
    }));
  };

  const getFilteredActividades = () => {
    switch (activeTab) {
      case 'activas':
        return actividades.filter(a => a.estado === 'activo');
      case 'completadas':
        return actividades.filter(a => a.estado === 'completado');
      case 'canceladas':
        return actividades.filter(a => a.estado === 'cancelado');
      default:
        return actividades;
    }
  };

  const getTipoLabel = (tipo) => {
    const tipos = {
      'actividad': 'Actividad General',
      'viaje': 'Viaje',
      'taller': 'Taller',
      'retiro': 'Retiro Espiritual',
      'conferencia': 'Conferencia',
      'visita': 'Visita'
    };
    return tipos[tipo] || tipo;
  };

  const getTipoBadgeColor = (tipo) => {
    const colores = {
      'viaje': 'bg-blue-100 text-blue-800',
      'taller': 'bg-purple-100 text-purple-800',
      'retiro': 'bg-amber-100 text-amber-800',
      'conferencia': 'bg-cyan-100 text-cyan-800',
      'visita': 'bg-pink-100 text-pink-800',
      'actividad': 'bg-emerald-100 text-emerald-800'
    };
    return colores[tipo] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const filteredActividades = getFilteredActividades();

  return (
    <div className="space-y-6" data-testid="actividades-extra-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <i className="fas fa-star text-emerald-500 mr-3"></i>
              Actividades Extracurriculares
            </h1>
            <p className="text-gray-600 mt-1">
              Gestiona viajes, talleres, retiros y actividades especiales para los estudiantes
            </p>
          </div>
        </div>
        
        {canCreate && (
          <Button 
            onClick={openCrear}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-6 py-3 h-auto"
            data-testid="btn-crear-actividad"
          >
            <i className="fas fa-plus mr-2"></i>
            Nueva Actividad
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-emerald-700">
              {actividades.filter(a => a.estado === 'activo').length}
            </div>
            <div className="text-sm text-emerald-600">Activas</div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-blue-700">
              {actividades.filter(a => {
                const fecha = new Date(a.fecha_inicio);
                const hoy = new Date();
                const proxSemana = new Date();
                proxSemana.setDate(hoy.getDate() + 7);
                return a.estado === 'activo' && fecha >= hoy && fecha <= proxSemana;
              }).length}
            </div>
            <div className="text-sm text-blue-600">Esta semana</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-gray-700">
              {actividades.filter(a => a.estado === 'completado').length}
            </div>
            <div className="text-sm text-gray-600">Completadas</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 text-center">
            <div className="text-3xl font-bold text-red-700">
              {actividades.filter(a => a.estado === 'cancelado').length}
            </div>
            <div className="text-sm text-red-600">Canceladas</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
          <TabsTrigger value="activas" className="data-[state=active]:bg-emerald-100">
            <i className="fas fa-check-circle mr-2"></i>
            Activas ({actividades.filter(a => a.estado === 'activo').length})
          </TabsTrigger>
          <TabsTrigger value="completadas">
            <i className="fas fa-flag-checkered mr-2"></i>
            Completadas ({actividades.filter(a => a.estado === 'completado').length})
          </TabsTrigger>
          <TabsTrigger value="canceladas">
            <i className="fas fa-times-circle mr-2"></i>
            Canceladas ({actividades.filter(a => a.estado === 'cancelado').length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredActividades.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredActividades.map((actividad) => {
                const fechaInicio = new Date(actividad.fecha_inicio);
                const esPasada = fechaInicio < new Date();
                
                return (
                  <Card 
                    key={actividad.id} 
                    className={`hover:shadow-lg transition-shadow cursor-pointer border-l-4 ${
                      actividad.estado === 'cancelado' ? 'border-l-red-500 opacity-60' :
                      actividad.estado === 'completado' ? 'border-l-gray-400' :
                      'border-l-emerald-500'
                    }`}
                    onClick={() => openDetalle(actividad)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <CardTitle className="text-lg flex items-center">
                          <i className="fas fa-star text-emerald-500 mr-2"></i>
                          {actividad.titulo}
                        </CardTitle>
                        <Badge className={getTipoBadgeColor(actividad.tipo)}>
                          {getTipoLabel(actividad.tipo)}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {actividad.descripcion}
                      </p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center text-gray-700">
                          <i className="fas fa-calendar-alt w-5 text-emerald-600"></i>
                          <span>{format(fechaInicio, "EEEE d 'de' MMMM, yyyy", { locale: es })}</span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <i className="fas fa-clock w-5 text-emerald-600"></i>
                          <span>{format(fechaInicio, 'HH:mm', { locale: es })} hrs</span>
                        </div>
                        {actividad.ubicacion && (
                          <div className="flex items-center text-gray-700">
                            <i className="fas fa-map-marker-alt w-5 text-emerald-600"></i>
                            <span className="truncate">{actividad.ubicacion}</span>
                          </div>
                        )}
                        <div className="flex items-center text-gray-700">
                          <i className="fas fa-users w-5 text-emerald-600"></i>
                          <span>
                            {actividad.destinatarios === 'todos' 
                              ? 'Todos' 
                              : `${actividad.estudiantes_seleccionados?.length || 0} estudiantes`}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 pt-3 border-t flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          Por: {actividad.creador_nombre}
                        </span>
                        {actividad.estado === 'activo' && (
                          <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                            <i className="fas fa-clipboard-check mr-1"></i>
                            {actividad.asistencias?.length || 0} asistencias
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <i className="fas fa-calendar-times text-5xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No hay actividades {activeTab}
              </h3>
              <p className="text-gray-500 mb-4">
                {activeTab === 'activas' && canCreate && 'Crea la primera actividad para tus estudiantes'}
                {activeTab === 'completadas' && 'Las actividades pasadas aparecerán aquí'}
                {activeTab === 'canceladas' && 'Las actividades canceladas aparecerán aquí'}
              </p>
              {activeTab === 'activas' && canCreate && (
                <Button onClick={openCrear} className="bg-emerald-600 hover:bg-emerald-700">
                  <i className="fas fa-plus mr-2"></i>
                  Crear Actividad
                </Button>
              )}
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-emerald-700 text-xl">
              <i className="fas fa-star mr-2"></i>
              {actividadEditando ? 'Editar Actividad' : 'Nueva Actividad Extracurricular'}
            </DialogTitle>
            <DialogDescription>
              Crea actividades como viajes, talleres o eventos especiales. Los estudiantes recibirán notificaciones automáticas.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="titulo">Título de la actividad *</Label>
              <Input
                id="titulo"
                value={form.titulo}
                onChange={(e) => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ej: Viaje a Colonia del Sacramento"
                data-testid="input-titulo"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción *</Label>
              <Textarea
                id="descripcion"
                value={form.descripcion}
                onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                placeholder="Describe los detalles de la actividad, qué deben traer, punto de encuentro, etc."
                rows={3}
                data-testid="input-descripcion"
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de actividad</Label>
              <Select value={form.tipo} onValueChange={(value) => setForm(prev => ({ ...prev, tipo: value }))}>
                <SelectTrigger data-testid="select-tipo">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="actividad">Actividad General</SelectItem>
                  <SelectItem value="viaje">Viaje</SelectItem>
                  <SelectItem value="taller">Taller</SelectItem>
                  <SelectItem value="retiro">Retiro Espiritual</SelectItem>
                  <SelectItem value="conferencia">Conferencia</SelectItem>
                  <SelectItem value="visita">Visita</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_inicio">Fecha inicio *</Label>
                <Input
                  id="fecha_inicio"
                  type="date"
                  value={form.fecha_inicio}
                  onChange={(e) => setForm(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                  data-testid="input-fecha-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_inicio">Hora inicio</Label>
                <Input
                  id="hora_inicio"
                  type="time"
                  value={form.hora_inicio}
                  onChange={(e) => setForm(prev => ({ ...prev, hora_inicio: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fecha_fin">Fecha fin *</Label>
                <Input
                  id="fecha_fin"
                  type="date"
                  value={form.fecha_fin}
                  onChange={(e) => setForm(prev => ({ ...prev, fecha_fin: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_fin">Hora fin</Label>
                <Input
                  id="hora_fin"
                  type="time"
                  value={form.hora_fin}
                  onChange={(e) => setForm(prev => ({ ...prev, hora_fin: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ubicacion">Ubicación</Label>
              <Input
                id="ubicacion"
                value={form.ubicacion}
                onChange={(e) => setForm(prev => ({ ...prev, ubicacion: e.target.value }))}
                placeholder="Ej: Plaza Independencia, Montevideo"
              />
            </div>

            <div className="space-y-2">
              <Label>Destinatarios</Label>
              <Select value={form.destinatarios} onValueChange={(value) => setForm(prev => ({ ...prev, destinatarios: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar destinatarios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos (Estudiantes y Profesores)</SelectItem>
                  <SelectItem value="seleccionados">Participantes seleccionados</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.destinatarios === 'seleccionados' && (
              <div className="space-y-2">
                <Label>Seleccionar participantes ({form.estudiantes_seleccionados.length} seleccionados)</Label>
                <div className="max-h-64 overflow-y-auto border rounded-lg p-3 space-y-1">
                  {participantes.length > 0 ? (
                    <>
                      {/* Profesores */}
                      {participantes.filter(p => p.rol === 'profesor').length > 0 && (
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-blue-600 mb-1 sticky top-0 bg-white py-1">
                            <i className="fas fa-chalkboard-teacher mr-1"></i> PROFESORES
                          </p>
                          {participantes.filter(p => p.rol === 'profesor').map((prof) => (
                            <div key={prof.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 rounded">
                              <Checkbox
                                id={`part-${prof.id}`}
                                checked={form.estudiantes_seleccionados.includes(prof.id)}
                                onCheckedChange={() => toggleEstudiante(prof.id)}
                              />
                              <label htmlFor={`part-${prof.id}`} className="text-sm cursor-pointer flex-1">
                                {prof.nombre} {prof.apellido}
                                <span className="text-blue-500 text-xs ml-1">(Profesor)</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Estudiantes */}
                      {participantes.filter(p => p.rol !== 'profesor').length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-emerald-600 mb-1 sticky top-0 bg-white py-1">
                            <i className="fas fa-user-graduate mr-1"></i> ESTUDIANTES
                          </p>
                          {participantes.filter(p => p.rol !== 'profesor').map((est) => (
                            <div key={est.id} className="flex items-center space-x-2 py-1 hover:bg-gray-50 rounded">
                              <Checkbox
                                id={`part-${est.id}`}
                                checked={form.estudiantes_seleccionados.includes(est.id)}
                                onCheckedChange={() => toggleEstudiante(est.id)}
                              />
                              <label htmlFor={`part-${est.id}`} className="text-sm cursor-pointer flex-1">
                                {est.nombre} {est.apellido}
                                <span className="text-gray-400 text-xs ml-1">({est.rol})</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">Cargando participantes...</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleGuardar} 
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  {actividadEditando ? 'Actualizar' : 'Crear Actividad'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Detalle */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader className="sticky top-0 bg-white pb-2 border-b">
            <DialogTitle className="flex items-center text-emerald-700">
              <i className="fas fa-star mr-2"></i>
              {actividadDetalle?.titulo}
            </DialogTitle>
          </DialogHeader>

          {actividadDetalle && (
            <div className="space-y-4 py-2">
              <Badge className={getTipoBadgeColor(actividadDetalle.tipo)}>
                {getTipoLabel(actividadDetalle.tipo)}
              </Badge>

              <div className="bg-emerald-50 p-3 rounded-lg space-y-1 text-sm">
                <p>
                  <i className="fas fa-calendar text-emerald-600 mr-2"></i>
                  <strong>Fecha:</strong> {format(new Date(actividadDetalle.fecha_inicio), "EEEE d 'de' MMMM, yyyy", { locale: es })}
                </p>
                <p>
                  <i className="fas fa-clock text-emerald-600 mr-2"></i>
                  <strong>Hora:</strong> {format(new Date(actividadDetalle.fecha_inicio), 'HH:mm', { locale: es })} hrs
                </p>
                {actividadDetalle.ubicacion && (
                  <p className="text-sm">
                    <i className="fas fa-map-marker-alt text-emerald-600 mr-2"></i>
                    <strong>Ubicación:</strong> {actividadDetalle.ubicacion}
                  </p>
                )}
                <p className="text-sm">
                  <i className="fas fa-user-tie text-emerald-600 mr-2"></i>
                  <strong>Creado por:</strong> {actividadDetalle.creador_nombre}
                </p>
              </div>

              <div>
                <h4 className="font-medium mb-1">Descripción</h4>
                <p className="text-sm text-gray-600">{actividadDetalle.descripcion}</p>
              </div>

              {canCreate && actividadDetalle.estado === 'activo' && (
                <>
                  <div className="border-t pt-3">
                    <h4 className="font-medium mb-2 flex items-center text-sm">
                      <i className="fas fa-qrcode text-emerald-600 mr-2"></i>
                      Código QR para Asistencia
                    </h4>
                    <div className="flex justify-center">
                      {qrCodeUrl && (
                        <div className="text-center">
                          <a 
                            href={`/asistencia-actividad/${actividadDetalle.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block cursor-pointer hover:opacity-90 transition-opacity"
                            title="Toca para marcar tu asistencia"
                          >
                            <img 
                              src={qrCodeUrl} 
                              alt="QR Asistencia" 
                              className="mx-auto border-2 border-emerald-300 rounded-lg shadow hover:shadow-lg hover:border-emerald-500 transition-all w-36 h-36" 
                            />
                          </a>
                          <p className="text-xs text-gray-500 mt-1">
                            Escanea o toca el QR
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <h4 className="font-medium mb-2 flex items-center text-sm">
                      <i className="fas fa-clipboard-check text-emerald-600 mr-2"></i>
                      Asistencia ({actividadDetalle.asistencias?.length || 0})
                    </h4>
                    {actividadDetalle.asistencias?.length > 0 ? (
                      <div className="max-h-24 overflow-y-auto space-y-1">
                        {actividadDetalle.asistencias.map((a, idx) => (
                          <div key={idx} className="flex items-center text-sm bg-gray-50 p-2 rounded">
                            <i className="fas fa-check-circle text-green-500 mr-2"></i>
                            {a.nombre || a.estudiante_nombre}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">Aún no hay asistencias</p>
                    )}
                  </div>

                  {/* Botones de acción para actividades activas */}
                  <div className="flex flex-wrap justify-between items-center gap-2 border-t pt-3 sticky bottom-0 bg-white pb-1">
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="btn-editar-actividad"
                      onClick={() => {
                        setShowDetailModal(false);
                        openEditar(actividadDetalle);
                      }}
                    >
                      <i className="fas fa-edit mr-1"></i>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="btn-cumplida-actividad"
                      onClick={() => handleCambiarEstado(actividadDetalle.id, 'completado', 'Actividad marcada como cumplida')}
                    >
                      <i className="fas fa-flag-checkered mr-1"></i>
                      Cumplida
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="btn-cancelar-actividad"
                      onClick={() => handleCancelar(actividadDetalle.id)}
                    >
                      <i className="fas fa-ban mr-1"></i>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}

              {/* Botones para actividades completadas o canceladas */}
              {canCreate && (actividadDetalle.estado === 'completado' || actividadDetalle.estado === 'cancelado') && (
                <div className="flex justify-between items-center gap-2 border-t pt-3 sticky bottom-0 bg-white pb-1">
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="btn-reactivar-actividad"
                    onClick={() => handleCambiarEstado(actividadDetalle.id, 'activo', 'Actividad reactivada')}
                  >
                    <i className="fas fa-redo mr-1"></i>
                    Reactivar
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    data-testid="btn-eliminar-actividad"
                    onClick={() => handleEliminar(actividadDetalle.id)}
                  >
                    <i className="fas fa-trash mr-1"></i>
                    Eliminar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActividadesExtracurriculares;
